import React, { FunctionComponent, useCallback, useMemo, useState, useEffect } from 'react';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WideContent from 'core/layout/content/wide-content';
import RedemptionsTable from './redemptions-table';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import Button from 'src/ui/atoms/button';
import { AdminApi, AdminProductListing, AdminProductsListPermissionEnum, ProductPermissionEnum } from 'src/api';
import AddRedemptionModal from './add-redemption-modal';
import useApiCall from 'src/hooks/use-api-call';
import { debounce } from 'lodash';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import { hasManageRedemptionsPermission, hasViewPermission } from '../../helpers/tokenPermissions';
import Spacer from 'src/ui/atoms/spacer';
import PageWrapper from 'src/core/layout/content/page-wrapper';

const IssuerRedemptionsPage: FunctionComponent = () => {
  const [isAddRedemptionOpen, setAddRedemptionOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [products, setProducts] = useState<AdminProductListing[]>([]);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const { currentUser } = useCurrentUserSelector();

  const hasPermission =
    hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
    hasViewPermission(currentUser) ||
    hasManageRedemptionsPermission(currentUser);

  const fetchProducts = useCallback(
    (searchQuery: string = '', action?: string) => {
      withApi(async () => {
        if (action === 'menu-close' && searchQuery === '') {
          return;
        }
        const response = await adminApi.adminProductsList({
          permission: AdminProductsListPermissionEnum.MANAGE_REDEMPTIONS,
          search: searchQuery,
          limit: 100,
        });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchProductValue = useCallback(
    (value: string, action?: string) => {
      fetchProducts(value, action);
    },
    [fetchProducts],
  );

  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refreshRedemptionsTable = useCallback(() => {
    setAddRedemptionOpen(false);
    setForceDataLoad(!forceDataLoad);
  }, [forceDataLoad]);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.issuerRedemptions" />}>
        {hasPermission &&
          currentUser?.permissionOnAtLeastOneProduct.includes(ProductPermissionEnum.MANAGE_REDEMPTIONS) && (
            <>
              <Button
                variant="primary"
                icon="plus"
                size="small"
                iconSize="small"
                iconColor="inherit"
                onClick={() => setAddRedemptionOpen(true)}
              >
                <Translate name="studioRedemptionsTable.addRedemption" />
              </Button>
              <Spacer y={4} />
            </>
          )}
        <RedemptionsTable
          forceDataLoad={forceDataLoad}
          hasPermission={hasPermission}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddRedemptionOpen && (
          <AddRedemptionModal
            products={products}
            onAddRedemption={refreshRedemptionsTable}
            onClose={() => setAddRedemptionOpen(false)}
            onSearchProduct={debouncedProductSearch}
          />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerRedemptionsPage, AppType.ISSUER);
